import { forwardRef } from 'react';
import { css } from '@emotion/react';
import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Chip, ChipProps, SpinningLoader } from '@frontend/design-system';
import { APPT_STATUS_INFO_MAP } from '../../constants';

export type AppointmentStatusChipProps = Partial<ChipProps> & {
  color: string;
  icon: IconName;
  chipVariant: ChipProps['variant'];
  label: string;
  isIconOnly?: boolean;
  isLoading?: boolean;
  bordered?: boolean;
  disabled?: boolean;
};

export type StatusChipProps = Omit<AppointmentStatusChipProps, 'color' | 'icon' | 'chipVariant' | 'label'> & {
  status?: string;
};

export const AppointmentStatusChip = forwardRef<HTMLDivElement, AppointmentStatusChipProps>(
  (
    {
      children,
      color,
      icon,
      chipVariant,
      label,
      isIconOnly = false,
      isLoading = false,
      bordered = false,
      disabled = false,
      ...rest
    },
    ref
  ) => {
    if (isLoading) {
      return <SpinningLoader size='xxs' css={spinnerStyle(color)} />;
    }

    const iconElement = <Icon name={icon} css={{ color, fontSize: isIconOnly ? '1.5rem' : undefined }} />;

    if (isIconOnly) {
      return (
        <Chip.Tag ref={ref} {...rest} variant={chipVariant} css={chipStyle({ bordered, compact: true, disabled })}>
          {iconElement}
        </Chip.Tag>
      );
    }

    return (
      <Chip.Tag
        ref={ref}
        leftElement={iconElement}
        variant={chipVariant}
        {...rest}
        css={chipStyle({ bordered, disabled })}
      >
        {label}
        {children}
      </Chip.Tag>
    );
  }
);

AppointmentStatusChip.displayName = 'AppointmentStatusChip';

export const StatusChip = forwardRef<HTMLDivElement, StatusChipProps>(({ status, ...rest }, ref) => {
  const mappedProps = APPT_STATUS_INFO_MAP[status as EventStatus];
  if (!mappedProps) return null;
  const { color, label, chipVariant, icon } = mappedProps;

  return (
    <AppointmentStatusChip ref={ref} color={color} label={label} chipVariant={chipVariant} icon={icon} {...rest} />
  );
});

StatusChip.displayName = 'StatusChip';

const chipStyle = ({
  bordered = false,
  compact = false,
  disabled = false,
}: {
  bordered?: boolean;
  compact?: boolean;
  disabled?: boolean;
}) =>
  css({
    maxWidth: 'max-content',
    border: bordered ? '1px solid' : 'none',
    padding: compact ? theme.spacing(0.25, 0.5) : theme.spacing(0.5, 1),
    cursor: disabled ? 'not-allowed' : 'pointer',
  });

const spinnerStyle = (color: string) =>
  css({
    borderRadius: theme.borderRadius.small,
    background: `color-mix(in srgb, ${color} 10%, ${theme.colors.white})`,
  });
